import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import './HMSFloatingActionMenu.css';

/**
 * HMSFloatingActionMenu
 *
 * Reusable floating action button + slide-in pill menu.
 *
 * Visually identical to the delivery screen's ActionButton:
 *  - 60x60 circular trigger (HMS primary blue)
 *  - rotates 45 degrees when open
 *  - per-item color pills slide in below it
 *
 * Drop it anywhere a screen needs a stacked set of primary actions.
 *
 * Props:
 *  - items: Array<{
 *      key:     string                 (required, unique)
 *      label:   string                 (text shown on the pill)
 *      icon:    FontAwesome icon       (optional)
 *      onClick: () => void             (required)
 *      color:   'blue' | 'green' | 'orange' | 'purple' | 'red' | 'teal'
 *               (optional, controls pill background)
 *      badge:   string | number        (optional, white pill on the right)
 *      disabled:boolean                (optional)
 *      title:   string                 (optional, native tooltip)
 *      hidden:  boolean                (optional, omit from menu)
 *    }>
 *  - label:    string  - text under the trigger ('Actions' by default)
 *  - icon:     FontAwesome icon - trigger glyph (faPlus default)
 *  - position: 'top-right' | 'bottom-right' (default 'top-right')
 *  - className: extra class on the container
 */
const HMSFloatingActionMenu = ({
  items,
  label = 'Actions',
  icon = faPlus,
  position = 'top-right',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const visibleItems = (items || []).filter((item) => item && !item.hidden);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscKey = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  if (visibleItems.length === 0) return null;

  const toggleMenu = () => setIsOpen((open) => !open);

  const handleItemClick = (item) => {
    if (item.disabled) return;
    item.onClick?.();
    setIsOpen(false);
  };

  const containerClass = [
    'hms-fab-container',
    `hms-fab-position-${position}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClass} ref={containerRef}>
      <button
        type="button"
        className={`hms-fab-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={toggleMenu}
        aria-label={`Open ${label.toLowerCase()} menu`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <span className="hms-fab-trigger-icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="hms-fab-trigger-label">{label}</span>
      </button>

      {isOpen && (
        <div className="hms-fab-menu" role="menu">
          {visibleItems.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={`hms-fab-item hms-fab-item-${item.color || 'blue'} ${item.disabled ? 'is-disabled' : ''}`}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              title={item.title || item.label}
            >
              {item.icon && (
                <span className="hms-fab-item-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
              )}
              <span className="hms-fab-item-label">{item.label}</span>
              {item.badge !== undefined && item.badge !== null && item.badge !== '' && (
                <span className="hms-fab-item-badge">{item.badge}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HMSFloatingActionMenu;
